import { createContext } from 'react';
import { supabase } from './supabase';
import type { User, Session } from '@/types/auth';
import { toast } from 'sonner';

export async function updateUserPassword(userId: string) {
  const { data, error } = await supabase.auth.admin.updateUserById(userId, {
    password: '%iJ5dA+Gdi2ci7V'
  });
  if (error) throw error;
  return data;
}

export async function signIn(email: string, password: string) {
  const { data: { session }, error: signInError } = await supabase.auth.signInWithPassword({
    email,
    password,
    options: {
      // Persist session in storage
      persistSession: true
    }
  });

  if (signInError) {
    console.error('Auth error:', signInError);
    throw new Error(signInError.message);
  }
  
  if (!session) {
    throw new Error('No session returned from authentication');
  }

  try {
    const { user: authUser } = session;
    if (!authUser) throw new Error('No user in session');
    
    // Wait a moment for session to be properly stored
    await new Promise(resolve => setTimeout(resolve, 100));

    // Try to get existing user profile
    const { data: userData, error: userError } = await supabase
      .from('sms_users')
      .select('*')
      .eq('id', authUser.id)
      .single();

    // If user doesn't exist in sms_users table, create them
    if (!userData && userError?.code === 'PGRST116') {
      // Use service role client for insert
      const serviceClient = supabase.auth.admin;
      if (!serviceClient) {
        throw new Error('Service role client not available');
      }

      const { data: newUser, error: createError } = await supabase
        .from('sms_users')
        .insert({
          id: authUser.id,
          email: authUser.email!,
          name: authUser.email!.split('@')[0],
          role: 'app.viewer'
        })
        .select()
        .single();

      if (createError) throw createError;
      
      toast.success('User profile created successfully');
      return {
        user: newUser,
        accessToken: session.access_token,
      };
    }

    if (userError) throw userError;
    if (!userData) throw new Error('Failed to create or load user profile');

    // Update last sign in time
    await supabase
      .from('sms_users')
      .update({ last_sign_in_at: new Date().toISOString() })
      .eq('id', userData.id);
    return {
      user: userData,
      accessToken: session.access_token,
    };
  } catch (error) {
    console.error('Error handling user profile:', error);
    await signOut();
    throw error instanceof Error ? error : new Error('Failed to handle user profile');
  }
}

export async function signOut() {
  const { error } = await supabase.auth.signOut({
    scope: 'global'
  });
  if (error) throw error;
}

export async function resetPassword(email: string) {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: 'https://moegy-teacher-reporter.netlify.app/auth/reset-password',
  });
  if (error) throw error;
}

export async function updatePassword(password: string) {
  const { error } = await supabase.auth.updateUser({ password });

  if (error) throw error;
}

export async function inviteUser(email: string, name: string, role: User['role']) {
  const { data, error } = await supabase.functions.invoke('invite-user', {
    body: { email, name, role },
  });
  if (error) throw error;
  return data;
}

export const AuthContext = createContext<{
  session: Session | null;
  isLoading: boolean;
}>({
  session: null,
  isLoading: true,
});